import React, { useEffect, useMemo, useState } from 'react'
import { Card, Button } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { appUrl, apiResource } from '../../utils'
import LoadingIcon from '../../components/loading/LoadingIcon'
import OficinaTabs from '../../components/oficina-tabs/OficinaTabs'
import CartProductosModal from './CartProductosModal'
import { useGet } from '../../hooks/process-data'
import { doRequestPost } from '../../api'
import { doToast } from '../../utils'
import { toast } from 'react-toastify'

export default function ProductosCatalogView() {
  const history = useHistory()
  const { sessionValidated, isCustomer } = useSelector(state => state.session)
  const [adding, setAdding] = useState(null)
  const [cartModalShow, setCartModalShow] = useState(false)

  const execute = !!(sessionValidated && isCustomer)

  const { rows: products, isProcessing, setReload } = useGet({
    url: '/product/public',
    execute
  })

  const { rows: cartData, setReload: setReloadCart } = useGet({
    url: '/product/cart',
    execute
  })

  const cartItems = useMemo(() => (cartData && cartData.items) || [], [cartData])
  const cartQtyBySku = useMemo(() => {
    const map = {}
    ;(cartItems || []).forEach(it => {
      map[it.id_product_sku] = (map[it.id_product_sku] || 0) + (it.qty || 0)
    })
    return map
  }, [cartItems])
  const cartCount = useMemo(() => cartItems.reduce((n, it) => n + (it.qty || 0), 0), [cartItems])

  const refreshCart = () => {
    setReloadCart(true)
    setReload(true)
  }

  useEffect(() => {
    if (sessionValidated && !isCustomer) {
      const redirect = encodeURIComponent(appUrl({ url: '/productos' }))
      history.push(appUrl({ url: '/cuenta/login?redirect=' + redirect }))
    } else if (sessionValidated && isCustomer) {
      setReload(true)
      setReloadCart(true)
    }
  }, [sessionValidated, isCustomer, history, setReload, setReloadCart])

  const handleAddToCart = async (idProductSku, qty = 1) => {
    setAdding(idProductSku)
    try {
      const res = await doRequestPost({
        url: '/product/cart/add',
        data: { p_id_product_sku: idProductSku, p_qty: qty }
      })
      doToast({ ...res, toast })
      if (res.response === 'success') refreshCart()
    } catch (e) {
      doToast({ response: 'danger', message: 'Error al agregar.', toast })
    } finally {
      setAdding(null)
    }
  }

  if (!sessionValidated || !isCustomer) {
    return (
      <div className="container py-4 d-flex justify-content-center">
        <LoadingIcon />
      </div>
    )
  }

  return (
    <div className="container py-4 py-lg-5">
      <OficinaTabs />
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
        <div>
          <h1 className="h4 mb-1">Productos</h1>
          <p className="text-muted mb-0">Elige merchandising y agrégalo a tu carrito.</p>
        </div>
        <Button
          variant="primary"
          className="btn-productos-style px-4 py-2"
          onClick={() => setCartModalShow(true)}
        >
          <i className="fa fa-shopping-cart me-2" />
          Ver carrito
          {cartCount > 0 && (
            <span className="badge bg-light text-dark ms-2">{cartCount}</span>
          )}
        </Button>
      </div>

      {isProcessing && (
        <div className="d-flex justify-content-center py-5">
          <LoadingIcon />
        </div>
      )}

      {!isProcessing && products && products.length === 0 && (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center py-5">
            <i className="fa fa-box-open fa-3x text-muted mb-3" />
            <p className="text-muted mb-0">No hay productos disponibles por el momento.</p>
          </Card.Body>
        </Card>
      )}

      {!isProcessing && products && products.length > 0 && (
        <div className="row g-4">
          {products.map(product => (
            <div key={product.id_product} className="col-md-6 col-lg-4">
              <Card className="h-100 shadow-sm border-0">
                {product.image_product && (
                  <Card.Img
                    variant="top"
                    src={apiResource({ url: product.image_product })}
                    style={{ height: 200, objectFit: 'cover' }}
                  />
                )}
                <Card.Body>
                  <Card.Title className="h6">{product.name_product}</Card.Title>
                  <p className="text-muted small mb-2">
                    {product.type_product?.name_type_product || ''}
                  </p>
                  {(product.skus || []).length === 0 && (
                    <p className="small text-warning mb-0">Sin stock</p>
                  )}
                  {(product.skus || []).map(sku => {
                    const inCartQty = cartQtyBySku[sku.id_product_sku] || 0
                    const stock = sku.stock_product_sku ?? 0
                    const isAdding = adding === sku.id_product_sku
                    return (
                      <div
                        key={sku.id_product_sku}
                        className="d-flex justify-content-between align-items-center border-bottom py-2 small product-sku-row"
                      >
                        <div className="flex-grow-1">
                          <span>
                            {sku.size_product_sku} / {sku.color_product_sku} — MXN {parseFloat(sku.price_product_sku).toFixed(2)}
                          </span>
                          {inCartQty > 0 && (
                            <small className="d-block text-success mt-1">
                              En carrito: {inCartQty}
                            </small>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="primary"
                          className="product-btn-add btn-productos-style px-3"
                          disabled={stock < 1 || isAdding}
                          onClick={() => handleAddToCart(sku.id_product_sku, 1)}
                        >
                          {isAdding ? (
                            <span className="d-inline-flex align-items-center">
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                              Agregando…
                            </span>
                          ) : (
                            'Agregar'
                          )}
                        </Button>
                      </div>
                    )
                  })}
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      )}

      <CartProductosModal
        show={cartModalShow}
        onHide={() => setCartModalShow(false)}
        items={cartItems}
        setReload={refreshCart}
      />
    </div>
  )
}
